import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import { Update as UpdateIcon } from '@mui/icons-material';
import newImportService from '../../services/newImportService';

/**
 * Admin tab: update one ADO work item and all its descendants.
 * User pastes ADO work item ID and clicks Update; the app fetches the item and descendants from ADO,
 * applies correct field mapping per type, saves to the WorkItem collection, and rebuilds hierarchy.
 */
const UpdateSingleItemAdmin = () => {
  const [workItemId, setWorkItemId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [result, setResult] = useState(null);

  const [filterUrl, setFilterUrl] = useState('');
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterMessage, setFilterMessage] = useState({ type: '', text: '' });
  const [filterResult, setFilterResult] = useState(null);

  const handleUpdate = async () => {
    const id = workItemId.trim();
    if (!id) {
      setMessage({ type: 'error', text: 'Please enter an ADO work item ID.' });
      setResult(null);
      return;
    }
    const num = parseInt(id, 10);
    if (isNaN(num) || num < 1) {
      setMessage({ type: 'error', text: 'Please enter a valid positive number (ADO work item ID).' });
      setResult(null);
      return;
    }

    setMessage({ type: '', text: '' });
    setResult(null);
    setLoading(true);

    try {
      const response = await newImportService.updateSingleItemFromAdo(id);
      if (response.success !== false && response.data) {
        setResult(response.data);
        setMessage({
          type: response.success ? 'success' : 'warning',
          text: response.message || `Processed ${response.data.totalProcessed ?? 0} item(s).`
        });
      } else {
        setMessage({
          type: 'error',
          text: response.message || 'Update request failed.'
        });
        setResult(response.data || null);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to update item from ADO.'
      });
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterUpdate = async () => {
    const url = filterUrl.trim();
    if (!url) {
      setFilterMessage({ type: 'error', text: 'Please paste an ADO filter URL.' });
      setFilterResult(null);
      return;
    }

    setFilterMessage({ type: '', text: '' });
    setFilterResult(null);
    setFilterLoading(true);

    try {
      const response = await newImportService.updateFromFilterFromAdo(url);
      if (response.success !== false && response.data) {
        setFilterResult(response.data);
        setFilterMessage({
          type: response.success ? 'success' : 'warning',
          text: response.message || `Processed ${response.data.totalProcessed ?? 0} item(s).`
        });
      } else {
        setFilterMessage({
          type: 'error',
          text: response.message || 'Update from filter failed.'
        });
        setFilterResult(response.data || null);
      }
    } catch (error) {
      setFilterMessage({
        type: 'error',
        text: error.message || 'Failed to update from ADO filter.'
      });
      setFilterResult(null);
    } finally {
      setFilterLoading(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, maxWidth: 560 }}>
        <Typography variant="h6" gutterBottom>
          Update single item from ADO
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter an ADO work item ID (any type: Epic, Feature, PBI, Bug, Task, etc.). The item and all its descendants will be fetched from ADO, mapped with the correct fields for each type, saved to the portal, and hierarchy will be rebuilt.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField
            label="ADO work item ID"
            placeholder="e.g. 123456"
            value={workItemId}
            onChange={(e) => setWorkItemId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <UpdateIcon />}
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? 'Updating…' : 'Update'}
          </Button>
        </Stack>
        {message.text && (
          <Alert severity={message.type === 'error' ? 'error' : message.type === 'warning' ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}
        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Result
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
              <Chip size="small" label={`Processed: ${result.totalProcessed ?? 0}`} />
              <Chip size="small" color="success" label={`Created: ${result.created ?? 0}`} />
              <Chip size="small" color="info" label={`Updated: ${result.updated ?? 0}`} />
              {(result.errors ?? 0) > 0 && (
                <Chip size="small" color="error" label={`Errors: ${result.errors}`} />
              )}
              {result.hierarchies != null && (
                <Chip size="small" label={`Hierarchies: ${result.hierarchies}`} />
              )}
            </Stack>
            {result.byType && Object.keys(result.byType).length > 0 && (
              <Typography variant="caption" display="block" color="text.secondary">
                By type: {Object.entries(result.byType)
                  .filter(([, v]) => v && (v.created > 0 || v.updated > 0 || v.failed > 0))
                  .map(([type, v]) => `${type} (${v.created ?? 0} created, ${v.updated ?? 0} updated${(v.failed ?? 0) > 0 ? `, ${v.failed} failed` : ''})`)
                  .join('; ')}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, maxWidth: 720, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Update from filter
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste an ADO filter URL below (saved query URL or work items URL). All work items matching the filter will be fetched from ADO with the relevant fields for each type, then saved to the portal (same mapping as single-item update).
        </Typography>
        <Stack direction="column" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField
            label="ADO filter URL"
            placeholder="e.g. https://dev.azure.com/org/project/_queries/query/{query-id} or .../_workitems?wiql=..."
            value={filterUrl}
            onChange={(e) => setFilterUrl(e.target.value)}
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            fullWidth
            sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
          />
          <Button
            variant="contained"
            startIcon={filterLoading ? <CircularProgress size={18} color="inherit" /> : <UpdateIcon />}
            onClick={handleFilterUpdate}
            disabled={filterLoading}
          >
            {filterLoading ? 'Updating…' : 'Update'}
          </Button>
        </Stack>
        {filterMessage.text && (
          <Alert severity={filterMessage.type === 'error' ? 'error' : filterMessage.type === 'warning' ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {filterMessage.text}
          </Alert>
        )}
        {filterResult && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Result
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
              <Chip size="small" label={`Processed: ${filterResult.totalProcessed ?? 0}`} />
              <Chip size="small" color="success" label={`Created: ${filterResult.created ?? 0}`} />
              <Chip size="small" color="info" label={`Updated: ${filterResult.updated ?? 0}`} />
              {(filterResult.errors ?? 0) > 0 && (
                <Chip size="small" color="error" label={`Errors: ${filterResult.errors}`} />
              )}
            </Stack>
            {filterResult.byType && Object.keys(filterResult.byType).length > 0 && (
              <Typography variant="caption" display="block" color="text.secondary">
                By type: {Object.entries(filterResult.byType)
                  .filter(([, v]) => v && (v.created > 0 || v.updated > 0 || v.failed > 0))
                  .map(([type, v]) => `${type} (${v.created ?? 0} created, ${v.updated ?? 0} updated${(v.failed ?? 0) > 0 ? `, ${v.failed} failed` : ''})`)
                  .join('; ')}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UpdateSingleItemAdmin;
